import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";

const WhatWeOffer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [activeCard, setActiveCard] = useState(0);

  const offerings = [
    {
      title: "Pratyahara Tool",
      description:
        "Crucial limb between outer and inner Yoga. A conscious withdrawal that creates space for inner awareness to unfold.",
      icon: "🪷",
      color: "from-forest-green to-forest-green/80",
      gradient: "from-blue-50 to-white",
      // features: [
      //   "Conscious Withdrawal",
      //   "Inner Awareness",
      //   "Bridging Practice",
      //   "Space Creation",
      // ],
      // badge: "ESSENTIAL",
    },
    {
      title: "Citta Shuddhi",
      description:
        "Loosen belief systems and minimize likes and dislikes through non-attachment. Transform mindset.",
      icon: "🌿",
      color: "from-copper-brown to-copper-brown/80",
      gradient: "from-green-50 to-white",
      // features: [
      //   "Non-attachment Practice",
      //   "Belief System Work",
      //   "Mindset Transformation",
      //   "Emotional Balance",
      // ],
      // badge: "TRANSFORMATIVE",
    },
    {
      title: "Purification of Subconscious",
      description:
        "Release distorted samskaras which fuel karma. Break free from compelling patterns of actions and reactions.",
      icon: "🔆",
      color: "from-soft-saffron to-soft-saffron/80",
      gradient: "from-orange-50 to-white",
      // features: [
      //   "Samskara Release",
      //   "Karma Cleansing",
      //   "Pattern Breaking",
      //   "Deep Purification",
      // ],
      // badge: "ADVANCED",
    },
    {
      title: "Cultivation of Witness",
      description:
        "Gain objectivity and a clear perspective. You are not your karma. You are not the ego that does or suffers or enjoys.",
      icon: "ૐ",
      color: "from-earth-ochre to-earth-ochre/80",
      gradient: "from-blue-50 to-green-50",
      // features: [
      //   "Objective Awareness",
      //   "Ego Transcendence",
      //   "Clear Perspective",
      //   "Self-Realization",
      // ],
      // badge: "PROFOUND",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-32 bg-gradient-to-br from-pale-green to-white overflow-hidden"
    >
      {/* Modern green-blue pattern background */}
      <div className="absolute inset-0 modern-green-blue-pattern pointer-events-none z-0"></div>
      {/* Pale blue overlay for subtle effect */}
      <div className="absolute inset-0 bg-pale-blue/20"></div>

      {/* Floating decorative elements */}
      <motion.div
        className="absolute right-10 top-20 w-40 h-40 rounded-full bg-gradient-to-br from-pale-blue/30 to-soft-saffron/20 blur-3xl hidden lg:block"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute left-10 bottom-20 w-32 h-32 rounded-full bg-gradient-to-tr from-pale-green/30 to-pale-blue/20 blur-2xl hidden lg:block"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3,
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Enhanced Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 1 }}
            className="text-center mb-20"
          >
            {/* Category badge */}
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pale-blue/20 border border-pale-blue/30 mb-8 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-soft-saffron animate-pulse"></div>
              <span className="text-sm font-medium text-forest-green uppercase tracking-wider">
                Our Offerings
              </span>
            </div>

            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-forest-green mb-8 drop-shadow-lg">
              What We <span className="text-pale-blue">Offer</span>
            </h2>
            {/* 
            <p className="text-xl md:text-2xl text-copper-brown/90 max-w-4xl mx-auto leading-relaxed font-light">
              Discover our comprehensive range of offerings designed to support
              your journey toward inner peace and conscious living through
              ancient wisdom.
            </p> */}

            {/* Decorative divider */}
            <div className="flex items-center justify-center gap-4">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-pale-blue"></div>
              <div className="w-3 h-3 rounded-full bg-pale-blue"></div>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-pale-blue"></div>
            </div>
          </motion.div>

          {/* Modern Cards Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {offerings.map((offering, index) => {
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  onHoverStart={() => setActiveCard(index)}
                  className="group cursor-pointer"
                >
                  <Card
                    className={`relative bg-gradient-to-br ${offering.gradient} backdrop-blur-sm border border-pale-blue/20 shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden h-full hover:scale-105`}
                  >
                    {/* Badge */}
                    {/* <div className="absolute top-4 right-4 z-20">
                      <span
                            className={`px-2 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${offering.color} shadow-lg`}
                          >
                            {offering.badge}
                          </span>
                    </div> */}

                    {/* Gradient overlay */}
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${offering.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                    ></div>

                    <CardContent className="p-6 lg:p-8 relative z-10 h-full flex flex-col">
                      {/* Icon and Title Section */}
                      <div className="mb-6">
                        <div
                          className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${offering.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-all duration-500 shadow-lg`}
                        >
                          <span className="text-2xl">{offering.icon}</span>
                        </div>

                        <h3 className="font-heading text-xl lg:text-2xl font-bold text-forest-green mb-3 group-hover:text-pale-blue transition-colors duration-300">
                          {offering.title}
                        </h3>

                        <p className="text-copper-brown/90 leading-relaxed text-sm font-light mb-4">
                          {offering.description}
                        </p>
                      </div>

                      {/* Features List */}
                      {/* <div className="flex-1 mb-4">
                        <h4 className="font-semibold text-forest-green mb-3 text-xs uppercase tracking-wider">
                          Key Features:
                        </h4>
                        <div className="space-y-2">
                          {offering.features.map((feature, featureIndex) => (
                            <div
                              key={featureIndex}
                              className="flex items-center gap-2"
                            >
                              <CheckCircle className="w-3 h-3 text-pale-blue flex-shrink-0" />
                              <span className="text-xs text-copper-brown/80">
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div> */}

                      {/* CTA Button */}
                      {/* <div className="flex items-center justify-between pt-4 border-t border-pale-blue/30">
                        <span className="text-xs font-medium text-forest-green/70 uppercase tracking-wider">
                          Learn More
                        </span>
                        <div className="flex items-center gap-2 text-forest-green group-hover:text-pale-blue transition-colors duration-300">
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        </div>
                      </div> */}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Bottom CTA Section */}
          {/* <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-center mt-20"
          >
            <div className="max-w-3xl mx-auto">
              <p className="text-copper-brown/90 text-lg leading-relaxed mb-8">
              Awaken Deep Rest with Yoga Nidra
              </p>
<div>
  <p className="text-copper-brown/90 text-lg leading-relaxed mb-8">Experience profound relaxation, conscious rest, and inner balance. Download the Cittasya app to begin your journey inward.</p>
</div>
              <a
                href="/offerings"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold bg-gradient-to-r from-pale-blue to-soft-saffron text-forest-green shadow-lg transition-all duration-300  hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-pale-blue/60 active:scale-95"
              >
                <span>Download the App</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div> */}
        </div>
      </div>
    </section>
  );
};

export default WhatWeOffer;